import {
  Status,
  CampaignPerformanceStatsQuery,
  WebsitePerformanceStatsQuery,
  PositionPerformanceStatsQuery,
  PublisherPerformanceStatsQuery,
  AdvertiserPerformanceStatsQuery,
  AgencyPerformanceStatsQuery,
  AgencyTimeseriesQuery,
  InventoryPerformanceStatsQuery,
  InventoryTimeseriesQuery,
  ProgrammaticCampaignPerformanceStatsQuery,
  ProgrammaticCampaignTimeseriesQuery,
  TradingDeskPerformanceStatsQuery,
  TradingDeskTimeseriesQuery,
  AdvertiserTimeseriesQuery,
  CampaignTimeseriesQuery,
  PositionTimeseriesQuery,
  WebsiteTimeseriesQuery,
  PublisherTimeseriesQuery,
  ResellerPublisherPerformanceStatsQuery,
  ResellerAdvertiserPerformanceStatsQuery,
  ResellerPublisherTimeseriesQuery,
  ResellerAdvertiserTimeseriesQuery,
  AdGroupStatus,
  AdGroupPerformanceStatsQuery,
  AdGroupTimeseriesQuery,
  Maybe,
  AdCopyStatus,
  AdCopyCreationStatus,
} from '../__generated__/graphql'
import type { FlatAdvertiserCampaign } from '../utils/hooks/performanceStats/performanceStatsUtils'

export enum StatsEntity {
  Websites = 'websites',
  Positions = 'positions',
  AdGroups = 'adGroups',
  Campaigns = 'campaigns',
  Publishers = 'publishers',
  Advertisers = 'advertisers',
  ResellerPublisher = 'resellerPublisher',
  ResellerAdvertiser = 'resellerAdvertiser',
  Agency = 'agency',
  Inventory = 'inventory',
  ProgrammaticCampaign = 'programmaticCampaign',
  TradingDesk = 'tradingDesk',
}

type AgencyPerformanceStats = NonNullable<AgencyPerformanceStatsQuery['agency']['performanceStats']>

export type AgencyPerformanceStatsWithTimeseries = AgencyPerformanceStats & {
  timeseries: AgencyTimeseriesQuery['agency']['timeseries']['timeseries']
}

export type FormattedAgencyPerformanceStats = AgencyPerformanceStatsWithTimeseries & {
  publishers?: AgencyPerformanceStatsWithTimeseries['publisherResellers'][number]['publishers']
}

type CompanyAdvertiserPerformanceStats = NonNullable<AdvertiserPerformanceStatsQuery['advertiser']['performanceStats']>

export interface FormattedCompanyAdvertiserPerformanceStats extends CompanyAdvertiserPerformanceStats {
  resellers?: CompanyAdvertiserPerformanceStats['publisherResellers']
  websites?: Array<
    CompanyAdvertiserPerformanceStats['publisherResellers'][number]['publishers'][number]['websites'][number] & {
      additionalInfo: string
    }
  >
  timeseries: AdvertiserTimeseriesQuery['advertiser']['timeseries']['timeseries']
}

type InventoryPerformanceStats = NonNullable<InventoryPerformanceStatsQuery['inventory']['performanceStats']>

export interface FormattedInventoryPerformanceStats extends InventoryPerformanceStats {
  resellers?: InventoryPerformanceStats['publisherResellers']
  websites: Array<
    InventoryPerformanceStats['publisherResellers'][number]['publishers'][number]['websites'][number] & {
      additionalInfo: string
    }
  >
  timeseries: InventoryTimeseriesQuery['inventory']['timeseries']['timeseries']
}

type ProgrammaticCampaignPerformanceStats = NonNullable<
  ProgrammaticCampaignPerformanceStatsQuery['campaignProgrammatic']['performanceStats']
>

export interface FormattedProgrammaticCampaignPerformanceStats extends ProgrammaticCampaignPerformanceStats {
  resellers?: ProgrammaticCampaignPerformanceStats['publisherResellers']
  websites: Array<
    ProgrammaticCampaignPerformanceStats['publisherResellers'][number]['publishers'][number]['websites'][number] & {
      additionalInfo: string
    }
  >
  timeseries: ProgrammaticCampaignTimeseriesQuery['campaignProgrammatic']['timeseries']['timeseries']
}

type TradingDeskPerformanceStats = NonNullable<TradingDeskPerformanceStatsQuery['tradingDesk']['performanceStats']>

export interface FormattedTradingDeskPerformanceStats extends TradingDeskPerformanceStats {
  resellers?: TradingDeskPerformanceStats['publisherResellers']
  websites: Array<
    TradingDeskPerformanceStats['publisherResellers'][number]['publishers'][number]['websites'][number] & {
      additionalInfo: string
    }
  >
  timeseries: TradingDeskTimeseriesQuery['tradingDesk']['timeseries']['timeseries']
}

type AdGroupPerformanceStats = NonNullable<AdGroupPerformanceStatsQuery['adGroup']['performanceStats']>

export interface FormattedAdGroupPerformanceStats {
  adCopies: AdGroupPerformanceStats['adCopies']
  devices: AdGroupPerformanceStats['devices']
  stats: AdGroupPerformanceStats['stats']
  websites: Array<
    AdGroupPerformanceStats['publisherResellers'][number]['publishers'][number]['websites'][number] & {
      additionalInfo: string
    }
  >
  timeseries: AdGroupTimeseriesQuery['adGroup']['timeseries']['timeseries']
}

export type PositionPerformanceStatsData = NonNullable<PositionPerformanceStatsQuery['position']['performanceStats']>

export type FormattedPositionPerformanceStats = {
  stats: PositionPerformanceStatsData['stats']
  devices: PositionPerformanceStatsData['devices']
  zones: PositionPerformanceStatsData['zones']
  resellers?: PositionPerformanceStatsData['advertiserResellers']
  adTemplates: PositionPerformanceStatsData['adTemplates']
  campaigns: PositionPerformanceStatsData['advertiserResellers'][number]['advertisers'][number]['campaigns']
  timeseries: PositionTimeseriesQuery['position']['timeseries']['timeseries']
}

export type CampaignPerformanceStatsData = NonNullable<CampaignPerformanceStatsQuery['campaign']['performanceStats']>

export type FormattedCampaignPerformanceStats = Omit<CampaignPerformanceStatsData, 'adGroups'> & {
  adGroups?: Array<
    Pick<NonNullable<CampaignPerformanceStatsData['adGroups']>[number], 'id' | 'name' | 'status' | 'stats'>
  >
  websites: Array<
    CampaignPerformanceStatsData['publisherResellers'][number]['publishers'][number]['websites'][number] & {
      additionalInfo: string
    }
  >
  timeseries: CampaignTimeseriesQuery['campaign']['timeseries']['timeseries']
}

export type WebsitePerformanceStatsData = NonNullable<WebsitePerformanceStatsQuery['website']['performanceStats']>

export type FormattedWebsitePerformanceStats = {
  stats: WebsitePerformanceStatsData['stats']
  resellers?: WebsitePerformanceStatsData['advertiserResellers']
  positions: WebsitePerformanceStatsData['positions']
  campaigns: WebsitePerformanceStatsData['advertiserResellers'][number]['advertisers'][number]['campaigns']
  timeseries: WebsiteTimeseriesQuery['website']['timeseries']['timeseries']
}

export type CompanyPublisherPerformanceStats = NonNullable<
  PublisherPerformanceStatsQuery['publisher']['performanceStats']
>

export type FormattedCompanyPublisherPerformanceStats = Omit<CompanyPublisherPerformanceStats, 'websites'> & {
  resellers?: CompanyPublisherPerformanceStats['advertiserResellers']
  websites?: CompanyPublisherPerformanceStats['websites']
  campaigns?: {
    name: string
    stats: CompanyPublisherPerformanceStats['advertiserResellers'][number]['advertisers'][number]['campaigns'][number]['stats']
  }[]
  timeseries: PublisherTimeseriesQuery['publisher']['timeseries']['timeseries']
}

export type ResellerPublisherPerformanceStats =
  ResellerPublisherPerformanceStatsQuery['reseller']['publisherPerformanceStats']
export type FormattedResellerPublisherPerformanceStats = ResellerPublisherPerformanceStats & {
  advertisers: ResellerPublisherPerformanceStats['advertiserResellers'][number]['advertisers']
  timeseries: ResellerPublisherTimeseriesQuery['reseller']['publisherTimeseries']['timeseries']
}

export type ResellerAdvertiserPerformanceStats =
  ResellerAdvertiserPerformanceStatsQuery['reseller']['advertiserPerformanceStats']

export type FormattedResellerAdvertiserPerformanceStats = ResellerAdvertiserPerformanceStats & {
  publishers: ResellerAdvertiserPerformanceStats['publisherResellers'][number]['publishers']
  campaigns: FlatAdvertiserCampaign[]
  timeseries: ResellerAdvertiserTimeseriesQuery['reseller']['advertiserTimeseries']['timeseries']
}

export type AdvertiserPerformanceStatsType =
  | AdvertiserPerformanceStatsQuery['advertiser']['performanceStats']['stats']
  | FormattedCompanyAdvertiserPerformanceStats
  | FormattedCampaignPerformanceStats
  | FormattedAdGroupPerformanceStats
export type PublisherPerformanceStatsType =
  | WebsitePerformanceStatsQuery['website']['performanceStats']['stats']
  | WebsitePerformanceStatsData
  | FormattedCompanyPublisherPerformanceStats
  | FormattedWebsitePerformanceStats
  | PositionPerformanceStatsData

export type AgencyPerformanceStatsType = AgencyPerformanceStats

export const BASE_STAT_DATA_TYPE = {
  TIMESERIES: 'timeseries',
}

export const COMPANY_BASE_STAT_DATA_TYPE = {
  RESELLERS: 'resellers',
  WEBSITES_AND_POSITIONS: 'websitesAndPositions',
}

export const WEBSITE_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  CAMPAIGNS: 'campaigns',
  POSITIONS: 'positions',
  RESELLERS: COMPANY_BASE_STAT_DATA_TYPE.RESELLERS,
}

export const POSITION_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  DEVICES: 'devices',
  ZONES: 'zones',
  CAMPAIGNS: 'campaigns',
  ADTEMPLATES: 'adTemplates',
  RESELLERS: COMPANY_BASE_STAT_DATA_TYPE.RESELLERS,
}

export const PUBLISHER_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  CAMPAIGNS: 'campaigns',
  ...COMPANY_BASE_STAT_DATA_TYPE,
}

export const AGENCY_DATA_TYPE = {
  TIMESERIES: BASE_STAT_DATA_TYPE.TIMESERIES,
  ADVERTISERS: 'advertisers',
  PUBLISHERS: 'publishers',
}

export const RESELLER_PUBLISHER_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  PUBLISHERS: 'publishers',
  ADVERTISERS: 'advertisers',
}

export const RESELLER_ADVERTISER_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  PUBLISHERS: 'publishers',
  ADVERTISERS: 'advertisers',
  CAMPAIGNS: 'campaigns',
}

export const ADVERTISER_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  CAMPAIGNS: 'campaigns',
  ...COMPANY_BASE_STAT_DATA_TYPE,
}

export const ADGROUP_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  DEVICES: 'devices',
  ADCOPIES: 'adcopies',
  WEBSITES_AND_POSITIONS: COMPANY_BASE_STAT_DATA_TYPE.WEBSITES_AND_POSITIONS,
}

export const CAMPAIGN_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  WEBSITES_AND_POSITIONS: COMPANY_BASE_STAT_DATA_TYPE.WEBSITES_AND_POSITIONS,
  ADGROUPS: 'adgroups',
}

export const INVENTORY_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  ...COMPANY_BASE_STAT_DATA_TYPE,
}

export const PROGRAMMATIC_CAMPAIGN_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  ...COMPANY_BASE_STAT_DATA_TYPE,
}

export const TRADING_DESK_DATA_TYPE = {
  ...BASE_STAT_DATA_TYPE,
  ...COMPANY_BASE_STAT_DATA_TYPE,
}

export type AdGroupsPerformanceStatsTableItem =
  CampaignPerformanceStatsQuery['campaign']['performanceStats']['adGroups'][number]['stats'] & {
    name: string
    status: AdGroupStatus
    dailyBudget?: Maybe<number> | string
  }

export type AdCopyPerformanceStatsTableItem =
  AdGroupPerformanceStatsQuery['adGroup']['performanceStats']['adCopies'][number]['stats'] & {
    id: string
    name: string
    image: string
    status: AdCopyStatus | AdCopyCreationStatus
    statusUpdatedAt: string
    title: string
    description: string
    callToAction: string
    advertiserName: string
  }

export type PositionInfos = {
  name: string
  channelType?: string
  status?: Status
  id?: string
}

export type PositionsPerformanceStatsTableItem =
  WebsitePerformanceStatsQuery['website']['performanceStats']['positions'][number]['stats'] & PositionInfos
